import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useTranslation } from '@/contexts/LanguageContext';
import SEO from '@/components/SEO';
import { Send, Mail, MessageSquare } from 'lucide-react';

export default function ContactPage() {
  const { t } = useTranslation('contact');
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error(t('fillAll'));
      return;
    }
    setLoading(true);
    try {
      await api.post('/contact', form);
      toast.success(t('sent'));
      setForm({ name: '', email: '', subject: '', message: '' });
    } catch {
      toast.error(t('sendError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <SEO title="Contact" description="Contactez l'équipe DestockPro. Nous sommes là pour répondre à vos questions sur la marketplace de déstockage professionnel." path="/contact" />
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="contact-page">
      <section className="text-center mb-10">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-[#1F3F72] text-white mb-4">
          <Mail className="w-6 h-6" />
        </div>
        <h1 className="font-heading text-3xl font-bold text-neutral-900">{t('title')}</h1>
        <p className="text-base text-neutral-500 mt-2">{t('subtitle')}</p>
      </section>

      <section className="bg-white border rounded-xl p-8" data-testid="contact-form-section">
        <div className="flex items-center gap-2 mb-6">
          <MessageSquare className="w-5 h-5 text-[#1F3F72]" />
          <p className="text-sm text-neutral-500">{t('formSubtitle')}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t('name')}</Label>
              <Input placeholder={t('namePlaceholder')} value={form.name} onChange={(e) => update('name', e.target.value)} data-testid="contact-name-input" />
            </div>
            <div className="space-y-2">
              <Label>{t('email')}</Label>
              <Input type="email" placeholder={t('emailPlaceholder')} value={form.email} onChange={(e) => update('email', e.target.value)} data-testid="contact-email-input" />
            </div>
          </div>
          <div className="space-y-2">
            <Label>{t('subject')}</Label>
            <Input placeholder={t('subjectPlaceholder')} value={form.subject} onChange={(e) => update('subject', e.target.value)} data-testid="contact-subject-input" />
          </div>
          <div className="space-y-2">
            <Label>{t('message')}</Label>
            <Textarea rows={5} placeholder={t('messagePlaceholder')} value={form.message} onChange={(e) => update('message', e.target.value)} data-testid="contact-message-input" />
          </div>
          <Button type="submit" className="bg-[#F25C05] hover:bg-[#d94f04] text-white" disabled={loading} data-testid="contact-submit-btn">
            {loading ? t('sending') : <><Send className="w-4 h-4 mr-2" /> {t('sendBtn')}</>}
          </Button>
        </form>
      </section>
    </div>
    </>
  );
}